"use client";

import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-8 items-center">
        {[...Array(5)].map((_, i) => <div key={i} data-index={i} className="h-5 w-20 bg-muted animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.slice(0, 5).map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-medium text-foreground hover:!bg-transparent focus:!bg-transparent hover:text-primary transition-colors relative after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-0 after:bg-primary after:transition-all after:duration-300 hover:after:w-full">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 bg-background md:hidden">
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between px-4 h-16 border-b">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1497483/img/united_mining_power_logo.png" alt="united mining power" className="h-7" />
          <Button variant="ghost" size="icon" onClick={onClose} className="hover:!bg-transparent focus:!bg-transparent">
            <X className="size-6" />
          </Button>
        </div>
        <nav className="flex-1 overflow-y-auto px-4 py-6">
          {loading ? <div className="flex flex-col gap-4">
              {[...Array(5)].map((_, i) => <div key={i} data-index={i} className="h-10 bg-muted animate-pulse rounded" />)}
            </div> : <div className="flex flex-col gap-2">
              {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onClose} className="text-base font-medium text-foreground hover:text-primary py-3 px-4 rounded-md hover:bg-muted transition-colors">
                  {item.label}
                </Link>)}
            </div>}
        </nav>
        <div className="px-4 py-6 border-t">
          <Button className="w-full bg-primary hover:bg-primary-hover text-foreground-primary hover:!bg-primary-hover focus:!bg-transparent focus-visible:ring-2 focus-visible:ring-ring" onClick={onClose}>
            Request Data-Room
          </Button>
        </div>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header className={`sticky top-0 z-40 w-full bg-background border-b transition-all duration-300 ${isScrolled ? "h-16 shadow-soft" : "h-[5.5rem]"}`}>
      <div className="container mx-auto px-4 h-full flex items-center justify-between">
        <Link to="/" className="flex-shrink-0 hover:!bg-transparent focus:!bg-transparent">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1497483/img/united_mining_power_logo.png" alt="united mining power" className="h-7" />
        </Link>

        <MenuProvider menu_id="17" className="flex-1 flex justify-center">
          <DesktopNav />
        </MenuProvider>

        <div className="flex items-center gap-4">
          <Button className="hidden md:inline-flex bg-primary hover:bg-background text-foreground-primary hover:text-foreground border border-transparent hover:border-primary transition-all hover:!bg-background focus:!bg-transparent focus-visible:ring-2 focus-visible:ring-ring">
            Request Data-Room
          </Button>

          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(true)} className="md:hidden hover:!bg-transparent focus:!bg-transparent">
            <Menu className="size-6" />
          </Button>
        </div>
      </div>

      <MenuProvider menu_id="17">
        <MobileNav isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>
    </header>;
}